import { useEffect, useState } from 'react'
import { Link, useRoute } from 'wouter'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ArrowLeft, Ban, CreditCard, Plus, RefreshCw, ShieldOff, Sliders, XCircle } from 'lucide-react'
import { toast } from 'sonner'
import { api } from '@/lib/api'
import { handleApiError } from '@/lib/api-error'
import type {
  AdminEmpresa,
  AdminEmpresaDetail,
  AdminPago,
  AdminPlan,
  AdminUser,
  Paginated,
} from '@/types/admin'

const ESTADO_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  activa: 'default',
  pendiente: 'secondary',
  suspendida: 'destructive',
  cancelada: 'outline',
}

function formatMoney(n?: number | null) {
  if (n === undefined || n === null) return '—'
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(n)
}

function formatDate(s?: string | null) {
  if (!s) return '—'
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return s
  return d.toLocaleDateString('es-CL')
}

export function EmpresaDetailPage() {
  const [, params] = useRoute('/empresas/:id')
  const id = params?.id

  const [empresa, setEmpresa] = useState<AdminEmpresa | null>(null)
  const [detail, setDetail] = useState<AdminEmpresaDetail | null>(null)
  const [pagos, setPagos] = useState<AdminPago[]>([])
  const [usuarios, setUsuarios] = useState<AdminUser[]>([])
  const [planes, setPlanes] = useState<AdminPlan[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Modals
  const [openCambiarPlan, setOpenCambiarPlan] = useState(false)
  const [openAjustarCupos, setOpenAjustarCupos] = useState(false)
  const [openCancelar, setOpenCancelar] = useState(false)
  const [openRegistrarPago, setOpenRegistrarPago] = useState(false)
  const [openBloqueo, setOpenBloqueo] = useState(false)
  const [openSuspenderApp, setOpenSuspenderApp] = useState(false)
  const [appSaving, setAppSaving] = useState(false)
  const [iaSaving, setIaSaving] = useState(false)

  const reload = async () => {
    if (!id) return
    setIsLoading(true)
    try {
      const [emp, pagosRes, planesRes] = await Promise.all([
        api.get<{ data: AdminEmpresaDetail }>(`/admin/empresas/${id}`),
        api.get<Paginated<AdminPago>>(`/admin/empresas/${id}/pagos?per_page=50`),
        api.get<{ data: AdminPlan[] }>('/admin/planes'),
      ])
      setEmpresa(emp.data.empresa)
      setDetail(emp.data)
      setPagos(pagosRes.data ?? [])
      setPlanes(planesRes.data ?? [])
      setUsuarios(emp.data.empresa.usuarios ?? [])
    } catch (err) {
      handleApiError(err, 'No se pudo cargar la empresa.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const actualizarAcceso = async (
    campo: 'acceso_app_habilitado' | 'acceso_ia_habilitado',
    valor: boolean,
    setSaving: (v: boolean) => void,
  ) => {
    if (!id) return
    setSaving(true)
    try {
      const res = await api.patch<{ data: AdminEmpresa }>(`/admin/empresas/${id}/accesos`, {
        [campo]: valor,
      })
      // El endpoint responde el modelo fresco (sin relaciones anidadas ni
      // campos computados como tiene_mandantes); mergeamos para no perder los
      // datos que trajo la carga inicial de la empresa.
      setEmpresa((prev) => (prev ? { ...prev, ...res.data } : res.data))
      if (campo === 'acceso_app_habilitado') {
        toast.success(
          valor
            ? 'Acceso a la app móvil habilitado.'
            : 'Acceso a la app móvil suspendido. Se cerraron las sesiones móviles activas de la empresa.',
        )
      } else {
        toast.success(
          valor ? 'Acceso a funciones de IA habilitado.' : 'Acceso a funciones de IA suspendido.',
        )
      }
    } catch (err) {
      handleApiError(
        err,
        campo === 'acceso_app_habilitado'
          ? 'No se pudo actualizar el acceso a la app móvil.'
          : 'No se pudo actualizar el acceso a funciones de IA.',
      )
    } finally {
      setSaving(false)
    }
  }

  const handleAppSwitchChange = (valor: boolean) => {
    if (!valor) {
      setOpenSuspenderApp(true)
      return
    }
    actualizarAcceso('acceso_app_habilitado', true, setAppSaving)
  }

  const confirmarSuspenderApp = async () => {
    setOpenSuspenderApp(false)
    await actualizarAcceso('acceso_app_habilitado', false, setAppSaving)
  }

  if (!id) return null

  const sus = detail?.suscripcion_activa
  const lic = detail?.licencias

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link href="/empresas">
            <ArrowLeft className="mr-2 h-4 w-4" /> Volver
          </Link>
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold tracking-tight">{empresa?.nombre_fantasia ?? 'Empresa'}</h1>
            {empresa && (
              <Badge variant={ESTADO_VARIANT[empresa.estado] ?? 'outline'}>{empresa.estado}</Badge>
            )}
            {empresa?.acceso_bloqueado && (
              <Badge variant="destructive">Acceso bloqueado</Badge>
            )}
          </div>
          {empresa && (
            <p className="text-muted-foreground mt-1 font-mono text-sm">
              {empresa.rut} {empresa.email ? `· ${empresa.email}` : ''}
            </p>
          )}
          {empresa?.acceso_bloqueado && empresa.motivo_bloqueo && (
            <p className="mt-0.5 text-sm text-muted-foreground">
              Motivo de bloqueo: {empresa.motivo_bloqueo}
            </p>
          )}
        </div>
      </div>

      {isLoading || !empresa ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">Cargando…</CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="suscripcion" className="space-y-4">
          <TabsList>
            <TabsTrigger value="suscripcion">Suscripción</TabsTrigger>
            <TabsTrigger value="pagos">Pagos</TabsTrigger>
            <TabsTrigger value="usuarios">Usuarios</TabsTrigger>
            <TabsTrigger value="licencias">Licencias</TabsTrigger>
          </TabsList>

          {/* SUSCRIPCION */}
          <TabsContent value="suscripcion">
            <Card>
              <CardHeader>
                <CardTitle>Suscripción actual</CardTitle>
                <CardDescription>Plan vigente, cupos y próximo cobro</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-3">
                  <div>
                    <Label className="text-muted-foreground text-xs">Plan</Label>
                    <p className="text-lg font-semibold">{sus?.plan?.nombre ?? '—'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Cupos</Label>
                    <p className="text-lg font-semibold">
                      {sus ? `${sus.cupos_usados ?? 0} / ${sus.cantidad_cupos}` : '—'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Próximo cobro</Label>
                    <p className="text-lg font-semibold">{formatDate(sus?.proximo_cobro)}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 pt-2">
                  <Button onClick={() => setOpenCambiarPlan(true)}>
                    <RefreshCw className="mr-2 h-4 w-4" /> Cambiar plan
                  </Button>
                  <Button variant="outline" onClick={() => setOpenAjustarCupos(true)} disabled={!sus}>
                    <Sliders className="mr-2 h-4 w-4" /> Ajustar cupos
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => setOpenCancelar(true)}
                    disabled={!sus || sus.estado === 'cancelada'}
                  >
                    <XCircle className="mr-2 h-4 w-4" /> Cancelar suscripción
                  </Button>
                  {empresa.acceso_bloqueado ? (
                    <Button variant="outline" onClick={() => setOpenBloqueo(true)}>
                      <ShieldOff className="mr-2 h-4 w-4" /> Desbloquear acceso
                    </Button>
                  ) : (
                    <Button variant="destructive" onClick={() => setOpenBloqueo(true)}>
                      <Ban className="mr-2 h-4 w-4" /> Bloquear acceso
                    </Button>
                  )}
                </div>

                <div className="space-y-3 border-t pt-4">
                  <Label className="text-muted-foreground text-xs">Accesos</Label>
                  <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                    <div className="space-y-0.5">
                      <p className="text-sm font-medium">Acceso a la app móvil</p>
                      <p className="text-sm text-muted-foreground">
                        Habilita o suspende el inicio de sesión de los trabajadores en la app
                        móvil. Al suspenderlo se cerrarán las sesiones móviles activas de esta
                        empresa.
                      </p>
                    </div>
                    <Switch
                      checked={empresa.acceso_app_habilitado}
                      disabled={appSaving}
                      onCheckedChange={handleAppSwitchChange}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                    <div className="space-y-0.5">
                      <p className="text-sm font-medium">Acceso a funciones de IA</p>
                      <p className="text-sm text-muted-foreground">
                        Habilita o suspende la generación de capacitaciones con inteligencia
                        artificial para esta empresa.
                      </p>
                    </div>
                    <Switch
                      checked={empresa.acceso_ia_habilitado}
                      disabled={iaSaving}
                      onCheckedChange={(valor) =>
                        actualizarAcceso('acceso_ia_habilitado', valor, setIaSaving)
                      }
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PAGOS */}
          <TabsContent value="pagos">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Histórico de pagos</CardTitle>
                  <CardDescription>Pagos registrados para esta empresa</CardDescription>
                </div>
                <Button onClick={() => setOpenRegistrarPago(true)}>
                  <Plus className="mr-2 h-4 w-4" /> Registrar pago offline
                </Button>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fecha</TableHead>
                        <TableHead>N° factura</TableHead>
                        <TableHead>Monto</TableHead>
                        <TableHead>Método</TableHead>
                        <TableHead>Notas</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pagos.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                            Sin pagos registrados.
                          </TableCell>
                        </TableRow>
                      ) : (
                        pagos.map((p) => (
                          <TableRow key={p.id}>
                            <TableCell>{formatDate(p.fecha_pago)}</TableCell>
                            <TableCell className="font-mono text-sm">{p.numero_factura}</TableCell>
                            <TableCell>{formatMoney(p.monto)}</TableCell>
                            <TableCell>{p.metodo_pago ?? '—'}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {p.notas ?? '—'}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* USUARIOS */}
          <TabsContent value="usuarios">
            <Card>
              <CardHeader>
                <CardTitle>Usuarios</CardTitle>
                <CardDescription>Personas con acceso al sistema dentro de esta empresa</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>RUT</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {usuarios.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                            Sin usuarios cargados.
                          </TableCell>
                        </TableRow>
                      ) : (
                        usuarios.map((u) => (
                          <TableRow key={u.id}>
                            <TableCell className="font-medium">{u.name}</TableCell>
                            <TableCell>{u.email}</TableCell>
                            <TableCell className="font-mono text-sm">{u.rut ?? '—'}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* LICENCIAS */}
          <TabsContent value="licencias">
            <Card>
              <CardHeader>
                <CardTitle>Licencias</CardTitle>
                <CardDescription>Resumen de uso de cupos</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-3">
                <div>
                  <Label className="text-muted-foreground text-xs">Totales</Label>
                  <p className="text-3xl font-bold">{lic?.total ?? sus?.cantidad_cupos ?? 0}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Usadas</Label>
                  <p className="text-3xl font-bold">{lic?.usadas ?? sus?.cupos_usados ?? 0}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Disponibles</Label>
                  <p className="text-3xl font-bold">
                    {lic?.disponibles ??
                      Math.max((sus?.cantidad_cupos ?? 0) - (sus?.cupos_usados ?? 0), 0)}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Modals */}
      <CambiarPlanDialog
        open={openCambiarPlan}
        onOpenChange={setOpenCambiarPlan}
        empresaId={id}
        planes={planes}
        currentPlanId={sus?.plan_id}
        onSuccess={reload}
      />
      <AjustarCuposDialog
        open={openAjustarCupos}
        onOpenChange={setOpenAjustarCupos}
        empresaId={id}
        currentCupos={sus?.cantidad_cupos ?? 0}
        onSuccess={reload}
      />
      <CancelarSuscripcionDialog
        open={openCancelar}
        onOpenChange={setOpenCancelar}
        empresaId={id}
        onSuccess={reload}
      />
      <RegistrarPagoDialog
        open={openRegistrarPago}
        onOpenChange={setOpenRegistrarPago}
        empresaId={id}
        onSuccess={reload}
      />
      <BloquearAccesoDialog
        open={openBloqueo}
        onOpenChange={setOpenBloqueo}
        empresaId={id}
        esBloqueada={empresa?.acceso_bloqueado ?? false}
        motivoActual={empresa?.motivo_bloqueo ?? null}
        onSuccess={reload}
      />
      <SuspenderAppAccesoDialog
        open={openSuspenderApp}
        onOpenChange={setOpenSuspenderApp}
        isSaving={appSaving}
        onConfirm={confirmarSuspenderApp}
      />
    </div>
  )
}

// ----- Modals -----

function CambiarPlanDialog({
  open,
  onOpenChange,
  empresaId,
  planes,
  currentPlanId,
  onSuccess,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  empresaId: string
  planes: AdminPlan[]
  currentPlanId?: number
  onSuccess: () => void
}) {
  const [planId, setPlanId] = useState<string>('')
  const [cupos, setCupos] = useState<string>('')
  const [proximoCobro, setProximoCobro] = useState<string>('')
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setPlanId(currentPlanId ? String(currentPlanId) : '')
      setCupos('')
      setProximoCobro('')
    }
  }, [open, currentPlanId])

  const submit = async () => {
    if (!planId) {
      toast.error('Selecciona un plan')
      return
    }
    setIsSaving(true)
    try {
      const payload: Record<string, unknown> = { plan_id: Number(planId) }
      if (cupos) payload.cantidad_cupos = Number(cupos)
      if (proximoCobro) payload.proximo_cobro = proximoCobro
      await api.post(`/admin/empresas/${empresaId}/suscripcion`, payload)
      toast.success('Plan actualizado.')
      onOpenChange(false)
      onSuccess()
    } catch (err) {
      handleApiError(err, 'No se pudo actualizar el plan.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <CreditCard className="inline mr-2 h-4 w-4" /> Cambiar plan
          </DialogTitle>
          <DialogDescription>Asignar o reemplazar el plan de suscripción.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Plan</Label>
            <Select value={planId} onValueChange={setPlanId}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar plan…" />
              </SelectTrigger>
              <SelectContent>
                {planes.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Cantidad de cupos (opcional)</Label>
            <Input
              type="number"
              min={0}
              placeholder="Usar el del plan"
              value={cupos}
              onChange={(e) => setCupos(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Próximo cobro (opcional)</Label>
            <Input type="date" value={proximoCobro} onChange={(e) => setProximoCobro(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={isSaving}>
            {isSaving ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function AjustarCuposDialog({
  open,
  onOpenChange,
  empresaId,
  currentCupos,
  onSuccess,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  empresaId: string
  currentCupos: number
  onSuccess: () => void
}) {
  const [cupos, setCupos] = useState<string>(String(currentCupos))
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (open) setCupos(String(currentCupos))
  }, [open, currentCupos])

  const submit = async () => {
    const n = Number(cupos)
    if (!Number.isFinite(n) || n < 0) {
      toast.error('Cantidad inválida')
      return
    }
    setIsSaving(true)
    try {
      await api.patch(`/admin/empresas/${empresaId}/suscripcion/cupos`, { cantidad_cupos: n })
      toast.success('Cupos actualizados.')
      onOpenChange(false)
      onSuccess()
    } catch (err) {
      handleApiError(err, 'No se pudo actualizar los cupos.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ajustar cupos</DialogTitle>
          <DialogDescription>Modificar la cantidad total de cupos disponibles.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Cantidad de cupos</Label>
          <Input type="number" min={0} value={cupos} onChange={(e) => setCupos(e.target.value)} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={isSaving}>
            {isSaving ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function CancelarSuscripcionDialog({
  open,
  onOpenChange,
  empresaId,
  onSuccess,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  empresaId: string
  onSuccess: () => void
}) {
  const [motivo, setMotivo] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (open) setMotivo('')
  }, [open])

  const submit = async () => {
    if (motivo.trim().length < 10) {
      toast.error('El motivo debe tener al menos 10 caracteres.')
      return
    }
    setIsSaving(true)
    try {
      await api.post(`/admin/empresas/${empresaId}/suscripcion/cancelar`, { motivo })
      toast.success('Suscripción cancelada.')
      onOpenChange(false)
      onSuccess()
    } catch (err) {
      handleApiError(err, 'No se pudo cancelar la suscripción.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancelar suscripción</DialogTitle>
          <DialogDescription>
            Esta acción dará de baja la suscripción activa de la empresa.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Motivo (mínimo 10 caracteres)</Label>
          <Textarea rows={4} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Volver
          </Button>
          <Button variant="destructive" onClick={submit} disabled={isSaving}>
            {isSaving ? 'Cancelando…' : 'Cancelar suscripción'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function RegistrarPagoDialog({
  open,
  onOpenChange,
  empresaId,
  onSuccess,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  empresaId: string
  onSuccess: () => void
}) {
  const [monto, setMonto] = useState('')
  const [numeroFactura, setNumeroFactura] = useState('')
  const [fechaPago, setFechaPago] = useState('')
  const [metodoPago, setMetodoPago] = useState('')
  const [notas, setNotas] = useState('')
  const [avanzar, setAvanzar] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setMonto('')
      setNumeroFactura('')
      setFechaPago('')
      setMetodoPago('')
      setNotas('')
      setAvanzar(false)
    }
  }, [open])

  const submit = async () => {
    if (!monto || !numeroFactura || !fechaPago) {
      toast.error('Monto, N° de factura y fecha son obligatorios.')
      return
    }
    setIsSaving(true)
    try {
      const payload: Record<string, unknown> = {
        monto: Number(monto),
        numero_factura: numeroFactura,
        fecha_pago: fechaPago,
        avanzar_proximo_cobro: avanzar,
      }
      if (metodoPago) payload.metodo_pago = metodoPago
      if (notas) payload.notas = notas
      await api.post(`/admin/empresas/${empresaId}/pagos`, payload)
      toast.success('Pago registrado.')
      onOpenChange(false)
      onSuccess()
    } catch (err) {
      handleApiError(err, 'No se pudo registrar el pago.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Registrar pago offline</DialogTitle>
          <DialogDescription>Carga manual de un pago recibido fuera de la plataforma.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Monto (CLP)</Label>
            <Input type="number" min={0} value={monto} onChange={(e) => setMonto(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>N° de factura</Label>
            <Input value={numeroFactura} onChange={(e) => setNumeroFactura(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Fecha de pago</Label>
            <Input type="date" value={fechaPago} onChange={(e) => setFechaPago(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Método de pago</Label>
            <Input
              placeholder="Transferencia, efectivo…"
              value={metodoPago}
              onChange={(e) => setMetodoPago(e.target.value)}
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label>Notas</Label>
            <Textarea rows={3} value={notas} onChange={(e) => setNotas(e.target.value)} />
          </div>
          <div className="flex items-center gap-2 sm:col-span-2">
            <Checkbox
              id="avanzar"
              checked={avanzar}
              onCheckedChange={(v) => setAvanzar(v === true)}
            />
            <Label htmlFor="avanzar" className="cursor-pointer">
              Avanzar fecha de próximo cobro
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={isSaving}>
            {isSaving ? 'Guardando…' : 'Registrar pago'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function BloquearAccesoDialog({
  open,
  onOpenChange,
  empresaId,
  esBloqueada,
  motivoActual,
  onSuccess,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  empresaId: string
  esBloqueada: boolean
  motivoActual: string | null
  onSuccess: () => void
}) {
  const [motivo, setMotivo] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (open) setMotivo('')
  }, [open])

  const submit = async () => {
    if (!esBloqueada && motivo.trim().length < 10) {
      toast.error('El motivo debe tener al menos 10 caracteres.')
      return
    }
    setIsSaving(true)
    try {
      if (esBloqueada) {
        await api.post(`/admin/empresas/${empresaId}/desbloquear`)
        toast.success('Acceso desbloqueado.')
      } else {
        await api.post(`/admin/empresas/${empresaId}/bloquear`, { motivo })
        toast.success('Acceso bloqueado.')
      }
      onOpenChange(false)
      onSuccess()
    } catch (err) {
      handleApiError(
        err,
        esBloqueada ? 'No se pudo desbloquear el acceso.' : 'No se pudo bloquear el acceso.',
      )
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {esBloqueada ? (
              <>
                <ShieldOff className="inline mr-2 h-4 w-4" /> Desbloquear acceso
              </>
            ) : (
              <>
                <Ban className="inline mr-2 h-4 w-4" /> Bloquear acceso
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {esBloqueada
              ? 'Al desbloquear, los usuarios de esta empresa podrán volver a iniciar sesión.'
              : 'Al bloquear, se revocarán todas las sesiones activas y los usuarios no podrán iniciar sesión.'}
          </DialogDescription>
        </DialogHeader>
        {esBloqueada ? (
          <div className="space-y-2">
            {motivoActual && (
              <p className="text-sm text-muted-foreground">
                <span className="font-medium">Motivo del bloqueo:</span> {motivoActual}
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <Label>Motivo (mínimo 10 caracteres)</Label>
            <Textarea
              rows={4}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Describe la razón del bloqueo…"
            />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Volver
          </Button>
          {esBloqueada ? (
            <Button onClick={submit} disabled={isSaving}>
              {isSaving ? 'Desbloqueando…' : 'Desbloquear'}
            </Button>
          ) : (
            <Button variant="destructive" onClick={submit} disabled={isSaving}>
              {isSaving ? 'Bloqueando…' : 'Bloquear acceso'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function SuspenderAppAccesoDialog({
  open,
  onOpenChange,
  isSaving,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  isSaving: boolean
  onConfirm: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Suspender acceso a la app móvil</DialogTitle>
          <DialogDescription>
            Los trabajadores de esta empresa no podrán iniciar sesión en la app móvil y se
            cerrarán todas las sesiones móviles activas. El acceso web no se ve afectado.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Volver
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isSaving}>
            {isSaving ? 'Suspendiendo…' : 'Suspender acceso'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
